import { Component, input, OnDestroy, OnInit, output } from '@angular/core';
import isEqual from 'lodash/isEqual';
import { Subject, takeUntil } from 'rxjs';
import {
  ConfirmModalComponent,
  TConfirmModalParams,
} from 'src/app/project/components/confirm-modal/confirm-modal.component';
import { ModalService } from 'src/app/project/components/modal/modal.service';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { EAdvancedFilterOptions } from '../../filters-advanced/models/advanced-filter-options.enum';
import {
  TSearchFilter,
  TSearchFilterRow,
} from '../../filters-advanced/models/advanced-filter.model';
import { TSearchAdvancedFilterOperator } from '../../points/point-search/point-search.consts';
import { CustomTableService } from '../../site/site-table/custom-table/custom-table.service';
import { QuickFilterService } from '../filters-api/search/quick-filter-service';
import { TFilters } from '../filters-api/search/quick-filter-service.consts';
import { FilterContainerService } from './filter-container.service';

@Component({
  selector: 'pp-filter-container',
  templateUrl: './filter-container.component.html',
  styleUrls: ['./filter-container.component.scss'],
  standalone: false,
})
export class FilterContainerComponent implements OnInit, OnDestroy {
  ppWorkspaceId = input<string>();
  quickFilters: TSearchFilter[];
  advancedFilters: TSearchFilterRow[] = [];
  advancedFilterOperator: TSearchAdvancedFilterOperator = 'AND';
  ppFiltersChanged = output<TFilters>();
  ppFilters = input<TFilters>();

  private readonly destroy$ = new Subject<void>();

  constructor(
    private quickFilterService: QuickFilterService,
    private modalService: ModalService,
    private translationPipe: TranslationPipe,
    private customTableService: CustomTableService,
    private filterContainerService: FilterContainerService,
  ) {}

  ngOnDestroy() {
    this.saveFilters();
    this.destroy$.next();
  }

  ngOnInit(): void {
    this.setFilters();
    this.subscribeToFilterChanges();
  }

  saveFilters(reload: boolean = true): void {
    const completeAdvancedFilters = this.getCompleteAdvancedFilters();
    const cachedFilters = this.quickFilterService.getFilters();

    if (
      isEqual(cachedFilters.quick, this.quickFilters) &&
      isEqual(cachedFilters.advanced, completeAdvancedFilters) &&
      cachedFilters.operator === this.advancedFilterOperator
    ) {
      return;
    }

    this.quickFilterService.setFilters({
      quick: this.quickFilters,
      advanced: completeAdvancedFilters,
      operator: this.advancedFilterOperator,
    });

    this.emitFiltersChange();

    if (reload) {
      this.customTableService.reloadTable();
    }
  }

  setFilters(): void {
    const filters = this.quickFilterService.getFilters();

    this.quickFilters = filters.quick;
    this.advancedFilterOperator = filters.operator ?? 'AND';

    const formattedAdvancedFilters =
      this.filterContainerService.formatAdvancedFiltersFromAPIResponse(filters.advanced);
    this.advancedFilters = formattedAdvancedFilters;
  }

  isFilterPreExisting(): { isMatching: boolean; match: TSearchFilter } {
    const match = this.quickFilters.find((quickFilter) =>
      this.advancedFilters.find((advancedFilter) => {
        const removedWhiteSpaceAdvancedFilters = advancedFilter?.keys
          ?.filter((key): key is string => !!key)
          .map((key) => key.replace(/ /g, '').toLowerCase());
        const removedWhiteSpaceQuickFilters = quickFilter?.keys
          ?.filter((key): key is string => !!key)
          .map((key) => key.replace(/ /g, '').toLowerCase());

        const excludedTagsKey =
          removedWhiteSpaceQuickFilters[0] === removedWhiteSpaceAdvancedFilters?.[0];

        const keysMatch =
          removedWhiteSpaceAdvancedFilters.length === removedWhiteSpaceQuickFilters.length &&
          removedWhiteSpaceAdvancedFilters.every((key) =>
            removedWhiteSpaceQuickFilters.find((quickKey) => quickKey === key),
          );

        return excludedTagsKey || keysMatch;
      }),
    );

    return { isMatching: !!match, match };
  }

  removeBothTagTypes(filterType: 'ADVANCED' | 'QUICK', key: string): void {
    switch (filterType) {
      case 'ADVANCED':
        this.advancedFilters = this.advancedFilters.filter((advancedFilter) => {
          const firstKey = advancedFilter.keys?.[0];

          return !firstKey || !key.includes(firstKey);
        });
        break;
      case 'QUICK':
        this.quickFilters = this.quickFilters.filter(
          (quickFilter) => !quickFilter.keys?.[0]?.toLowerCase().includes('tags'),
        );
        break;
    }
  }

  removeFilterFromAdvancedFilters(key: string): void {
    if (key.includes('Tags')) {
      this.removeBothTagTypes('ADVANCED', key);
    } else {
      this.advancedFilters = this.advancedFilters.filter(
        (advancedFilter) => advancedFilter.keys?.[0]?.toLowerCase() !== key.toLowerCase(),
      );
    }
  }

  removeFilterFromQuickFilters(key: string): void {
    if (key.includes('Tags')) {
      this.removeBothTagTypes('QUICK', key);
    } else {
      this.quickFilters = this.quickFilters.filter(
        (quickFilter) => quickFilter.keys?.[0]?.toLowerCase() !== key.toLowerCase(),
      );
    }
  }

  handleAdvancedFilterChange(): void {
    const { match, isMatching } = this.isFilterPreExisting();
    if (isMatching) {
      const modalData: TConfirmModalParams = {
        message: this.translationPipe.transform('already_in_quick_filters'),
        secondaryMessage: this.translationPipe.transform('using_filter_will_remove_from_quick'),
        heading: this.translationPipe.transform('filter_already_in_use'),
        boldText: true,
        boldSecondaryText: false,
        extraPadding: true,
        confirmText: this.translationPipe.transform('use_in_advanced'),
        cancelText: this.translationPipe.transform('keep_in_quick'),
      };
      this.modalService.setData(modalData);

      this.modalService.showModal(ConfirmModalComponent, {
        blur: true,
        closeWarning: true,
        onBeforeClose: (cancelled) => {
          if (cancelled) {
            this.removeFilterFromAdvancedFilters(match.keys[0]);
          } else {
            this.removeFilterFromQuickFilters(match.keys[0]);
          }

          return Promise.resolve(true);
        },
      });
    }

    this.emitFiltersChange();
  }

  handleQuickFilterChange(): void {
    const { match, isMatching } = this.isFilterPreExisting();

    if (isMatching) {
      const modalData: TConfirmModalParams = {
        secondaryMessage: this.translationPipe.transform('already_in_advanced_filters'),
        message: this.translationPipe.transform('using_filter_will_remove_from_advanced'),
        heading: this.translationPipe.transform('filter_already_in_use'),
        boldText: true,
        boldSecondaryText: false,
        extraPadding: true,
        confirmText: this.translationPipe.transform('use_in_quick'),
        cancelText: this.translationPipe.transform('keep_in_advanced'),
      };
      this.modalService.setData(modalData);

      this.modalService.showModal(ConfirmModalComponent, {
        blur: true,

        onBeforeClose: (cancelled) => {
          if (cancelled) {
            this.removeFilterFromQuickFilters(match.keys[0]);
          } else {
            this.removeFilterFromAdvancedFilters(match.keys[0]);
          }

          return Promise.resolve(true);
        },
      });
    }

    this.emitFiltersChange();
  }

  private subscribeToFilterChanges(): void {
    this.quickFilterService
      .getFiltersChange$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((newFilters) => {
        this.quickFilters = newFilters.quick;
        this.advancedFilters = newFilters.advanced;
        this.advancedFilterOperator = newFilters.operator;
      });
  }

  private getCompleteAdvancedFilters(): TSearchFilterRow[] {
    return this.advancedFilters.filter((filter) => {
      if (!filter.operator || !filter.keys?.length) {
        return false;
      }

      const option = filter.item?.option;

      if (
        option === EAdvancedFilterOptions.IS_EMPTY ||
        option === EAdvancedFilterOptions.IS_NOT_EMPTY
      ) {
        return true;
      }

      return filter.values?.length > 0;
    });
  }

  private emitFiltersChange(): void {
    this.ppFiltersChanged.emit({
      quick: this.quickFilters,
      advanced: this.getCompleteAdvancedFilters(),
      operator: this.advancedFilterOperator,
    });
  }
}
